// covered-by: frontends/carrie/src/ask-v2/rail/__tests__/collectRailContent.test.ts
import {
  type AskPunttChat,
  type AskPunttMessage,
} from "@mg/schemas/src/christo/askPuntt";
import { type AskPunttProject } from "@mg/schemas/src/christo/askPunttProject";
import { defaultAskPunttConnectors } from "@mg/schemas/src/commons/enterprise";

import {
  type AskV2RailConnector,
  type AskV2RailContent,
  type AskV2RailDocument,
  type AskV2RailOutputItem,
  type AskV2RailProgressItem,
} from "./types";

import { type GetProjectReviewsResponse } from "../../services/askPunttProjectFeed.types";
import { matchToolToConnectorId } from "../../utils/askPunttKnownConnectorTools";

const CONNECTOR_NAME_BY_ID = new Map(
  defaultAskPunttConnectors.map((connector) => [
    connector.connectorId,
    connector.name,
  ]),
);

function hasCanvas(message: AskPunttMessage): boolean {
  const blocks = message.canvasContent?.blocks;
  return (
    (Array.isArray(blocks) && blocks.length > 0) ||
    message.canvasLoading === true
  );
}

function canvasTitle(message: AskPunttMessage, index: number): string {
  const outputType = message.canvasContent?.outputType;
  if (outputType) {
    return outputType
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }
  return `Document ${index + 1}`;
}

function collectConnectors(
  messages: readonly AskPunttMessage[],
): AskV2RailConnector[] {
  const seen = new Map<string, AskV2RailConnector>();
  for (const message of messages) {
    for (const step of message.agentSteps ?? []) {
      if (step.type !== "tool_call" && step.type !== "tool_result") continue;
      const toolName =
        step.type === "tool_result"
          ? step.toolName?.trim() || step.tool
          : step.tool;
      const args =
        step.type === "tool_call" && step.args && typeof step.args === "object"
          ? (step.args as Record<string, unknown>)
          : undefined;
      const id = matchToolToConnectorId(toolName, step.displayName ?? "", args);
      if (!id || seen.has(id)) continue;
      seen.set(id, {
        id,
        name: CONNECTOR_NAME_BY_ID.get(id) ?? id,
      });
    }
  }
  return Array.from(seen.values());
}

function collectDocuments(
  files: AskPunttProject["files"] | undefined,
): AskV2RailDocument[] {
  return (files ?? []).map((file) => {
    const latest = file.versions?.[file.versions.length - 1];
    return {
      id: file.fileId,
      name: file.name,
      type: file.type,
      versionNumber: latest?.versionNumber ?? file.currentVersion,
    };
  });
}

function stageStatus(status: string): AskV2RailProgressItem["status"] {
  if (status === "done") return "done";
  if (status === "active") return "active";
  return "pending";
}

/** Default empty review state — not worth a progress rail section on its own. */
function isMeaningfulProgressStatus(status: string | undefined): boolean {
  const normalized = status?.trim().toLowerCase();
  return Boolean(normalized && normalized !== "idle");
}

export function collectRailContent(input: {
  project?: Pick<
    AskPunttProject,
    "description" | "context" | "upcomingWork" | "files"
  > | null;
  chat?: Pick<
    AskPunttChat,
    "reviewStatus" | "reviewChecklist" | "reviewStages" | "projectData"
  > | null;
  messages?: readonly AskPunttMessage[];
  reviews?: GetProjectReviewsResponse | null;
}): AskV2RailContent {
  const messages = input.messages ?? [];
  const reviews = input.reviews?.reviews ?? [];
  const inReview = reviews.filter((item) => item.status === "in_review");
  const finishedReviews = reviews.filter((item) => item.status !== "in_review");

  const progressItems: AskV2RailProgressItem[] = [];
  if (input.chat?.reviewStages?.length) {
    for (const stage of input.chat.reviewStages) {
      progressItems.push({
        id: `stage-${stage.name}`,
        label: stage.name,
        detail: stage.detail?.[0],
        status: stageStatus(stage.status),
      });
    }
  } else if (input.chat?.reviewChecklist?.length) {
    input.chat.reviewChecklist.forEach((item, index) => {
      progressItems.push({
        id: `check-${index}`,
        label: item.label,
        detail: item.sub,
        status: "done",
      });
    });
  } else {
    for (const item of inReview) {
      progressItems.push({
        id: `review-${item.id}`,
        label: item.name,
        detail:
          item.versionNumber != null ? `v${item.versionNumber}` : undefined,
        status: "active",
      });
    }
  }

  // Prefer whichever source carries a meaningful (non-"idle") status: the
  // reviews panel can sit at its "idle" default while the chat has already
  // moved to "IN REVIEW", and `??` would let that idle default shadow it.
  const reviewsStatus = input.reviews?.status;
  const chatStatus = input.chat?.reviewStatus ?? undefined;
  const progressStatus = isMeaningfulProgressStatus(reviewsStatus)
    ? reviewsStatus
    : isMeaningfulProgressStatus(chatStatus)
      ? chatStatus
      : (reviewsStatus ?? chatStatus);
  const showProgress =
    progressItems.length > 0 || isMeaningfulProgressStatus(progressStatus);
  const progress = showProgress
    ? {
        status: isMeaningfulProgressStatus(progressStatus)
          ? progressStatus
          : undefined,
        items: progressItems,
      }
    : null;

  const outputItems: AskV2RailOutputItem[] = [];
  const seenReviewIds = new Set<string>();
  // In-review reviews render as in-flight progress items, not output cards.
  // Seed their ids so a mirrored assistant message (viewReviewId/viewTicketId)
  // for the same review does not leak a duplicate "Review" card into output.
  for (const item of inReview) {
    seenReviewIds.add(item.id);
    if (item.ticketId) seenReviewIds.add(item.ticketId);
  }
  for (const item of finishedReviews) {
    seenReviewIds.add(item.id);
    if (item.ticketId) seenReviewIds.add(item.ticketId);
    outputItems.push({
      id: item.id,
      kind: "review",
      title: item.title ?? item.name,
      thumbnailUrl: item.thumbnailUrl,
      versionNumber: item.versionNumber,
      aiCommentCount: item.aiCommentCount,
      humanCommentCount: item.humanCommentCount,
    });
  }

  let canvasIndex = 0;
  for (const message of messages) {
    if (message.role !== "assistant") continue;
    const reviewId = message.viewReviewId;
    if (reviewId && !seenReviewIds.has(reviewId)) {
      seenReviewIds.add(reviewId);
      outputItems.push({
        id: reviewId,
        kind: "review",
        title: "Review",
        isGenerating: message.status === "pending",
      });
    }
    if (hasCanvas(message)) {
      outputItems.push({
        id: message._id,
        kind: "canvas",
        title: canvasTitle(message, canvasIndex),
        isGenerating: message.canvasLoading === true,
      });
      canvasIndex += 1;
    }
  }

  const output = outputItems.length > 0 ? { items: outputItems } : null;

  const overview =
    input.project?.description?.trim() ||
    input.project?.context?.trim() ||
    input.chat?.projectData?.description?.trim() ||
    "";
  const upcomingWork = input.project?.upcomingWork?.trim() || "";
  const connectors = collectConnectors(messages);
  const documents = collectDocuments(input.project?.files);
  const context =
    overview || upcomingWork || connectors.length > 0 || documents.length > 0
      ? {
          overview: overview || undefined,
          upcomingWork: upcomingWork || undefined,
          connectors,
          documents,
        }
      : null;

  return { progress, output, context };
}

export function hasRailContent(content: AskV2RailContent): boolean {
  return (
    content.progress != null ||
    content.output != null ||
    content.context != null
  );
}
